import { createResolverRegistry } from '../resolvers/resolverRegistry';
import { toLoading } from '../resolvers/definition/resolverState';
import { createEntityLifecycleManager } from '../lifecycleManager/entityLifecycleManager';
import { create } from 'event-emitter';
import { lifecycleRules } from '../resolvers/decorators/lifecycleRules';
export const createController = ({
  httpClient,
  globalDecorators = [],
  createDevtools
}) => {
  const devtoolsCallbacks = {};
  const stateEmitter = create();
  const entityDefinitions = new Map();
  const registerEntityDef = entity => {
    const current = entityDefinitions.get(entity.typeName);
    if (current !== entity) {
      var _devtoolsCallbacks$on;
      entityDefinitions.set(entity.typeName, entity);
      (_devtoolsCallbacks$on = devtoolsCallbacks.onRegisterEntityDefinition) === null || _devtoolsCallbacks$on === void 0 || _devtoolsCallbacks$on.call(devtoolsCallbacks, entity);
    }
  };
  const resolverRegistry = createResolverRegistry({
    httpClient,
    globalDecorators: [lifecycleRules, ...globalDecorators]
  });
  const lifecycleManager = createEntityLifecycleManager({
    onStateChange: (entity, instanceKey, state) => {
      var _devtoolsCallbacks$on2;
      stateEmitter.emit(instanceKey, state);
      (_devtoolsCallbacks$on2 = devtoolsCallbacks.onStateChange) === null || _devtoolsCallbacks$on2 === void 0 || _devtoolsCallbacks$on2.call(devtoolsCallbacks, entity.typeName, instanceKey);
    },
    onInvalidate: (entity, instanceKey) => {
      var _devtoolsCallbacks$on3;
      if (resolverRegistry.isAttached({
        instanceKey,
        entity
      })) {
        resolverRegistry.refresh({
          instanceKey,
          entity
        });
      } else {
        // No resolver attached, manually mark this entity as stale for next attachment
        lifecycleManager.markStale(entity, instanceKey);
      }
      (_devtoolsCallbacks$on3 = devtoolsCallbacks.onInvalidate) === null || _devtoolsCallbacks$on3 === void 0 || _devtoolsCallbacks$on3.call(devtoolsCallbacks, entity.typeName, instanceKey);
    }
  });
  const watch = ({
    entity,
    params = {},
    instanceKey = entity.identifyInstance(params)
  }, callback) => {
    registerEntityDef(entity);

    // Attach if first subscriber
    if (stateEmitter.count(instanceKey) === 0) {
      var _devtoolsCallbacks$on4;
      const emptyInitial = toLoading()();
      const getState = () => lifecycleManager.getState(entity, instanceKey);
      resolverRegistry.attach({
        entity,
        instanceKey,
        params,
        getState: () => getState() || emptyInitial,
        onResolverUpdate: resolverState => lifecycleManager.handleStateChange(entity, instanceKey, resolverState)
      });
      const state = getState();
      if (!state || state.stale || state.loading) {
        resolverRegistry.refresh({
          instanceKey,
          entity
        });
      }
      (_devtoolsCallbacks$on4 = devtoolsCallbacks.onAttachmentChange) === null || _devtoolsCallbacks$on4 === void 0 || _devtoolsCallbacks$on4.call(devtoolsCallbacks, instanceKey, true);
    }
    const off = stateEmitter.on(instanceKey, callback !== null && callback !== void 0 ? callback : () => {});
    return () => {
      // Detach if last subscriber
      if (off() === 0) {
        var _devtoolsCallbacks$on5;
        resolverRegistry.detach({
          instanceKey,
          entity
        });
        (_devtoolsCallbacks$on5 = devtoolsCallbacks.onAttachmentChange) === null || _devtoolsCallbacks$on5 === void 0 || _devtoolsCallbacks$on5.call(devtoolsCallbacks, instanceKey, false);
        // TODO: Queue for cache GC
      }
    };
  };
  createDevtools === null || createDevtools === void 0 || createDevtools({
    entityDefinitions,
    lifecycleManager,
    resolverRegistry,
    devtoolsCallbacks
  });
  return {
    watch,
    invalidate: ({
      entity,
      params = {},
      instanceKey = entity.identifyInstance(params)
    }) => {
      registerEntityDef(entity);
      return lifecycleManager.invalidate(entity, instanceKey);
    },
    getState: ({
      entity,
      params = {},
      instanceKey = entity.identifyInstance(params)
    }) => {
      registerEntityDef(entity);
      return lifecycleManager.getState(entity, instanceKey);
    },
    setState: ({
      entity,
      state,
      params = {},
      instanceKey = entity.identifyInstance(params)
    }) => {
      registerEntityDef(entity);
      const nextState = typeof state === 'function' ? state(lifecycleManager.getState(entity, instanceKey)) : state;
      return lifecycleManager.handleStateChange(entity, instanceKey, nextState);
    }
  };
};